import {
  useGetSensor,
  useGetSensorHistory,
  useGetSensorAggregates,
  useUpdateSensorFavorite,
  useGetMonotonicOffsetHistory,
  SensorsHealthStatus,
  getGetSensorQueryKey,
  getListSensorsQueryKey,
  type SensorsSensor,
} from '@smarthome/api';
import { formatRelativeTime, ROUTES, useSensorStream } from '@smarthome/ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertCircle,
  ArrowLeft,
  Bell,
  Calendar,
  Clock,
  Copy,
  Hash,
  History,
  Pencil,
  RefreshCw,
  Ruler,
  Server,
  Star,
  TrendingUp,
} from 'lucide-react';
import { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import {
  formatReportingDuration,
  getReportingStabilityLabel,
  getReportingStabilityTone,
} from './reportingSummary';

import { SensorEditModal } from '@/components/SensorEditModal';
import { SensorChart, HistogramChart, Tooltip } from '@/components/ui';

type TimeRange = '1h' | '6h' | '24h' | '7d' | '30d' | 'custom';
type ChartType = 'line' | 'histogram';
type BucketSize = '5m' | '15m' | '1h' | '1d';

interface SensorChartPrefs {
  range: Exclude<TimeRange, 'custom'>;
  visualization: ChartType;
  bucketSize: BucketSize;
}

const SENSOR_CHART_PREFS_KEY = (sensorId: string) => `sensor-chart-prefs-${sensorId}`;

function loadSensorChartPrefs(sensorId: string): Partial<SensorChartPrefs> | null {
  try {
    const stored = localStorage.getItem(SENSOR_CHART_PREFS_KEY(sensorId));
    if (stored) {
      return JSON.parse(stored) as Partial<SensorChartPrefs>;
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

function saveSensorChartPrefs(sensorId: string, prefs: SensorChartPrefs) {
  try {
    localStorage.setItem(SENSOR_CHART_PREFS_KEY(sensorId), JSON.stringify(prefs));
  } catch {
    // Ignore storage errors
  }
}

const TIME_RANGES: { value: Exclude<TimeRange, 'custom'>; label: string }[] = [
  { value: '1h', label: '1h' },
  { value: '6h', label: '6h' },
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
];

const BUCKET_SIZES: { value: BucketSize; label: string }[] = [
  { value: '5m', label: '5m' },
  { value: '15m', label: '15m' },
  { value: '1h', label: '1h' },
  { value: '1d', label: '1d' },
];

const INTEGER_FORMATTER = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const ONE_DECIMAL_FORMATTER = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

function formatValue(value?: number): string {
  if (value === undefined || value === null) return '—';

  if (Number.isInteger(value)) {
    return INTEGER_FORMATTER.format(value);
  }
  return ONE_DECIMAL_FORMATTER.format(value);
}

function formatUnit(unit?: string): string {
  return unit || '';
}

const VALID_TIME_RANGES = ['1h', '6h', '24h', '7d', '30d', 'custom'] as const;

function isValidTimeRange(value: string | null): value is TimeRange {
  return value !== null && VALID_TIME_RANGES.includes(value as TimeRange);
}

const VALID_CHART_TYPES: ChartType[] = ['line', 'histogram'];
function isValidChartType(value: string | null): value is ChartType {
  return value !== null && VALID_CHART_TYPES.includes(value as ChartType);
}

const VALID_BUCKET_SIZES: BucketSize[] = ['5m', '15m', '1h', '1d'];
function isValidBucketSize(value: string | null): value is BucketSize {
  return value !== null && VALID_BUCKET_SIZES.includes(value as BucketSize);
}

export function SensorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse time range from URL, defaulting to '24h'
  const timeRange = useMemo((): TimeRange => {
    const range = searchParams.get('range');
    return isValidTimeRange(range) ? range : '24h';
  }, [searchParams]);

  // Parse custom range from URL if in custom mode
  const customRange = useMemo((): { start: string; end: string } | null => {
    if (timeRange !== 'custom') return null;
    const start = searchParams.get('start');
    const end = searchParams.get('end');
    if (start && end) {
      return { start, end };
    }
    return null;
  }, [timeRange, searchParams]);

  // Track current time for chart display - updated frequently for smooth scrolling
  const [displayTime, setDisplayTime] = useState(() => Date.now());

  // Track when we last fetched data - updated on sensor events to fetch new data
  const [queryTime, setQueryTime] = useState(() => Date.now());

  // Parse chart type from URL, defaulting to 'line'
  const chartType = useMemo((): ChartType => {
    const type = searchParams.get('chart');
    return isValidChartType(type) ? type : 'line';
  }, [searchParams]);

  // Parse bucket size from URL, defaulting to '1h'
  const bucketSize = useMemo((): BucketSize => {
    const bucket = searchParams.get('bucket');
    return isValidBucketSize(bucket) ? bucket : '1h';
  }, [searchParams]);

  // Load preferences from localStorage on initial mount (if no URL params set)
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (hasInitialized.current || !id) return;
    hasInitialized.current = true;

    // Check if URL has explicit chart preferences (not just defaults)
    const hasUrlPrefs =
      searchParams.has('range') || searchParams.has('chart') || searchParams.has('bucket');
    if (hasUrlPrefs) return;

    // Load from localStorage
    const stored = loadSensorChartPrefs(id);
    if (!stored) return;

    // Apply stored preferences to URL
    const updates: Record<string, string> = {};
    if (stored.range && isValidTimeRange(stored.range)) {
      updates['range'] = stored.range;
    }
    if (stored.visualization && isValidChartType(stored.visualization)) {
      updates['chart'] = stored.visualization;
    }
    if (stored.bucketSize && isValidBucketSize(stored.bucketSize)) {
      updates['bucket'] = stored.bucketSize;
    }

    if (Object.keys(updates).length > 0) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          next.set(key, value);
        }
        return next;
      });
    }
  }, [id, searchParams, setSearchParams]);

  // Update display time every 10 seconds for preset ranges (smooth chart edge movement)
  useEffect(() => {
    if (timeRange === 'custom') return;

    const interval = setInterval(() => {
      setDisplayTime(Date.now());
    }, 10000);

    return () => clearInterval(interval);
  }, [timeRange]);

  // Calculate the time range duration in ms for the current preset
  const rangeDurationMs = useMemo(() => {
    switch (timeRange) {
      case '1h':
        return 60 * 60 * 1000;
      case '6h':
        return 6 * 60 * 60 * 1000;
      case '24h':
        return 24 * 60 * 60 * 1000;
      case '7d':
        return 7 * 24 * 60 * 60 * 1000;
      case '30d':
        return 30 * 24 * 60 * 60 * 1000;
      default:
        return 24 * 60 * 60 * 1000;
    }
  }, [timeRange]);

  // API query times - updates when timeRange changes or when queryTime is bumped (sensor event)
  const { queryStartTime, queryEndTime } = useMemo(() => {
    if (timeRange === 'custom' && customRange) {
      return { queryStartTime: customRange.start, queryEndTime: customRange.end };
    }
    // Use queryTime as the end point to fetch up to "now" when sensor events arrive
    const endMs = queryTime;
    const startMs = endMs - rangeDurationMs;
    return {
      queryStartTime: new Date(startMs).toISOString(),
      queryEndTime: new Date(endMs).toISOString(),
    };
  }, [timeRange, customRange, rangeDurationMs, queryTime]);

  // Chart display times - updates frequently for smooth scrolling, doesn't trigger API refetch
  const { chartStartTime, chartEndTime } = useMemo(() => {
    if (timeRange === 'custom' && customRange) {
      return { chartStartTime: customRange.start, chartEndTime: customRange.end };
    }
    // Use displayTime for smooth visual updates
    const endMs = displayTime;
    const startMs = endMs - rangeDurationMs;
    return {
      chartStartTime: new Date(startMs).toISOString(),
      chartEndTime: new Date(endMs).toISOString(),
    };
  }, [timeRange, customRange, rangeDurationMs, displayTime]);

  // Fetch sensor details
  const {
    data: sensorData,
    isLoading: isSensorLoading,
    isError: isSensorError,
  } = useGetSensor(id || '', {
    query: {
      enabled: !!id,
      refetchInterval: 30000,
    },
  });

  // Favorite toggle mutation
  const { mutate: updateFavorite } = useUpdateSensorFavorite({
    mutation: {
      onMutate: async ({ id: sensorId, data: favoriteData }) => {
        await queryClient.cancelQueries({ queryKey: getGetSensorQueryKey(sensorId) });
        const previousData = queryClient.getQueryData<{ sensor: SensorsSensor }>(
          getGetSensorQueryKey(sensorId)
        );
        queryClient.setQueryData<{ sensor: SensorsSensor }>(
          getGetSensorQueryKey(sensorId),
          (old) => {
            if (!old?.sensor) return old;
            return { ...old, sensor: { ...old.sensor, isFavorite: favoriteData.isFavorite } };
          }
        );
        return { previousData };
      },
      onError: (_err, { id: sensorId }, context) => {
        if (context?.previousData) {
          queryClient.setQueryData(getGetSensorQueryKey(sensorId), context.previousData);
        }
      },
      onSuccess: () => {
        // Invalidate list view cache so it fetches fresh data
        queryClient.invalidateQueries({ queryKey: getListSensorsQueryKey() });
      },
    },
  });

  // Fetch monotonic offset history if sensor is monotonic
  const { data: offsetHistoryData } = useGetMonotonicOffsetHistory(id || '', {
    query: {
      enabled: !!id && !!sensorData?.sensor?.isMonotonic,
    },
  });

  const handleToggleFavorite = useCallback(() => {
    if (id && sensorData?.sensor) {
      updateFavorite({ id, data: { isFavorite: !sensorData.sensor.isFavorite } });
    }
  }, [id, sensorData?.sensor, updateFavorite]);

  // Fetch history with maxPoints parameter for server-side downsampling (for line chart)
  const {
    data: historyData,
    isLoading: isHistoryLoading,
    refetch: refetchHistory,
    isFetching: isHistoryFetching,
  } = useGetSensorHistory(
    id || '',
    {
      start: queryStartTime,
      end: queryEndTime,
      maxPoints: 1000, // Request up to 1000 points, server will downsample if needed
    },
    {
      query: {
        enabled: !!id && chartType === 'line',
        // Don't auto-refetch for custom (zoomed) ranges - user is viewing a specific time window
        refetchInterval: timeRange === 'custom' ? false : 60000,
        // Keep previous data while fetching new data to prevent flashing
        placeholderData: (prev) => prev,
      },
    }
  );

  // Fetch aggregated delta data for histogram chart
  const {
    data: aggregateData,
    isLoading: isAggregateLoading,
    refetch: refetchAggregate,
    isFetching: isAggregateFetching,
  } = useGetSensorAggregates(
    id || '',
    {
      start: queryStartTime,
      end: queryEndTime,
      interval: bucketSize,
      aggregation: 'delta',
    },
    {
      query: {
        enabled: !!id && chartType === 'histogram',
        // Don't auto-refetch for custom (zoomed) ranges
        refetchInterval: timeRange === 'custom' ? false : 60000,
        // Keep previous data while fetching new data to prevent flashing
        placeholderData: (prev) => prev,
      },
    }
  );

  // Combined refetch function that works for both chart types
  const refetchData = useCallback(() => {
    if (chartType === 'line') {
      refetchHistory();
    } else {
      refetchAggregate();
    }
  }, [chartType, refetchHistory, refetchAggregate]);

  // Combined loading/fetching state
  const isDataLoading = chartType === 'line' ? isHistoryLoading : isAggregateLoading;
  const isDataFetching = chartType === 'line' ? isHistoryFetching : isAggregateFetching;

  // Subscribe to real-time updates
  const handleSensorEvent = useCallback(() => {
    // Invalidate sensor query to update current value immediately
    if (id) {
      queryClient.invalidateQueries({ queryKey: getGetSensorQueryKey(id) });
    }
    // Update query time to extend the fetch window to include new data
    // This will trigger a refetch with the new end time
    // For custom (zoomed) ranges, don't update the graph - user is looking at a specific time window
    if (timeRange !== 'custom') {
      const now = Date.now();
      setQueryTime(now);
      setDisplayTime(now);
    }
  }, [queryClient, id, timeRange]);

  const { isConnected } = useSensorStream({
    filter: id,
    enabled: !!id,
    onEvent: handleSensorEvent,
  });

  // Helper to update search params while preserving others
  const updateSearchParams = useCallback(
    (updates: Record<string, string>, remove?: string[]) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        // Apply updates
        for (const [key, value] of Object.entries(updates)) {
          next.set(key, value);
        }
        // Remove specified keys
        if (remove) {
          for (const key of remove) {
            next.delete(key);
          }
        }
        return next;
      });
    },
    [setSearchParams]
  );

  // Handle zoom - when user drags on chart
  const handleZoom = useCallback(
    (start: Date, end: Date) => {
      updateSearchParams({
        range: 'custom',
        start: start.toISOString(),
        end: end.toISOString(),
      });
    },
    [updateSearchParams]
  );

  // Handle reset zoom - when user double-clicks
  const handleResetZoom = useCallback(() => {
    updateSearchParams({ range: '24h' }, ['start', 'end']);
  }, [updateSearchParams]);

  // Handle preset time range change
  const handleTimeRangeChange = useCallback(
    (range: Exclude<TimeRange, 'custom'>) => {
      updateSearchParams({ range }, ['start', 'end']);
      if (id) {
        saveSensorChartPrefs(id, { range, visualization: chartType, bucketSize });
      }
    },
    [updateSearchParams, id, chartType, bucketSize]
  );

  // Handle chart type change
  const handleChartTypeChange = useCallback(
    (type: ChartType) => {
      if (type === 'line') {
        // Remove chart param when switching to default (line)
        updateSearchParams({}, ['chart', 'bucket']);
      } else {
        updateSearchParams({ chart: type });
      }
      if (id) {
        const effectiveRange = timeRange === 'custom' ? '24h' : timeRange;
        saveSensorChartPrefs(id, { range: effectiveRange, visualization: type, bucketSize });
      }
    },
    [updateSearchParams, id, timeRange, bucketSize]
  );

  // Handle bucket size change
  const handleBucketSizeChange = useCallback(
    (bucket: BucketSize) => {
      if (bucket === '1h') {
        // Remove bucket param when switching to default (1h)
        updateSearchParams({}, ['bucket']);
      } else {
        updateSearchParams({ bucket });
      }
      if (id) {
        const effectiveRange = timeRange === 'custom' ? '24h' : timeRange;
        saveSensorChartPrefs(id, {
          range: effectiveRange,
          visualization: chartType,
          bucketSize: bucket,
        });
      }
    },
    [updateSearchParams, id, timeRange, chartType]
  );

  const sensor = sensorData?.sensor;
  const reportingSummary = sensor?.reportingSummary;

  // Edit modal state - holds a snapshot of sensor data to avoid re-renders from live updates
  const [editingSensor, setEditingSensor] = useState<SensorsSensor | null>(null);

  const handleOpenEditModal = useCallback(() => {
    if (sensor) {
      setEditingSensor(sensor);
    }
  }, [sensor]);

  const handleCloseEditModal = useCallback(() => {
    setEditingSensor(null);
  }, []);

  const historyValues = useMemo(() => historyData?.values || [], [historyData?.values]);
  const aggregateValues = useMemo(
    () => aggregateData?.aggregates || [],
    [aggregateData?.aggregates]
  );

  // Keep track of last valid stats to prevent layout jumps during loading
  const lastStatsRef = useRef<{ min: number; max: number; avg: number } | null>(null);

  // Calculate statistics from the data (only for line chart mode)
  const stats = useMemo(() => {
    if (chartType === 'histogram') {
      // For histogram, show total usage in the period
      if (!aggregateValues.length) return lastStatsRef.current;
      const values = aggregateValues.map((v) => v.value ?? 0);
      const total = values.reduce((a, b) => a + b, 0);
      const min = Math.min(...values);
      const max = Math.max(...values);
      const newStats = { min, max, avg: total };
      lastStatsRef.current = newStats;
      return newStats;
    }

    if (!historyValues.length) return lastStatsRef.current;

    const values = historyValues.map((v) => v.value ?? 0);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const avg = values.reduce((a, b) => a + b, 0) / values.length;

    const newStats = { min, max, avg };
    lastStatsRef.current = newStats;
    return newStats;
  }, [chartType, historyValues, aggregateValues]);

  // Helper function to get status indicator colors based on health status
  const getHealthStatusColor = (status?: string) => {
    switch (status) {
      case SensorsHealthStatus.HealthStatusLive:
        return 'bg-emerald-500 shadow-sm shadow-emerald-500/50';
      case SensorsHealthStatus.HealthStatusStale:
        return 'bg-yellow-500 shadow-sm shadow-yellow-500/50';
      case SensorsHealthStatus.HealthStatusMissing:
        return 'bg-red-500 shadow-sm shadow-red-500/50';
      default:
        return 'bg-gray-300 dark:bg-zinc-600';
    }
  };

  const getHealthStatusLabel = (status?: string) => {
    switch (status) {
      case SensorsHealthStatus.HealthStatusLive:
        return 'Live';
      case SensorsHealthStatus.HealthStatusStale:
        return 'Stale';
      case SensorsHealthStatus.HealthStatusMissing:
        return 'Missing';
      default:
        return 'Unknown';
    }
  };

  // Convert history data to chart format (for line chart)
  const lineChartData = useMemo(
    () =>
      historyValues.map((v) => ({
        timestamp: v.timestamp || '',
        value: v.value ?? null,
      })),
    [historyValues]
  );

  // Convert aggregate data to chart format (for histogram)
  const histogramChartData = useMemo(
    () =>
      aggregateValues.map((v) => ({
        timestamp: v.timestamp || '',
        value: v.value ?? null,
      })),
    [aggregateValues]
  );

  // Data point count for display
  const dataPointCount = chartType === 'line' ? historyValues.length : aggregateValues.length;

  if (isSensorLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
      </div>
    );
  }

  if (isSensorError || !sensor) {
    return (
      <div className="space-y-6">
        <Link
          to={ROUTES.SENSORS}
          className="inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Sensors
        </Link>
        <div className="rounded-xl border border-gray-200 bg-white p-6 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10">
            <AlertCircle className="h-6 w-6 text-red-400" />
          </div>
          <p className="text-sm text-gray-600 dark:text-zinc-400">
            {isSensorError ? 'Failed to load sensor' : 'Sensor not found'}
          </p>
          <Link
            to={ROUTES.SENSORS}
            className="mt-3 inline-block text-sm text-blue-500 hover:text-blue-400"
          >
            Back to sensors
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to={ROUTES.SENSORS}
        className="inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Sensors
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              {sensor.name || sensor.humanId || 'Unnamed Sensor'}
            </h1>
            <Tooltip
              content={
                <div className="text-center">
                  <div className="font-medium">{getHealthStatusLabel(sensor.healthStatus)}</div>
                  <div className="text-xs text-gray-400">
                    {sensor.healthStatus === SensorsHealthStatus.HealthStatusLive &&
                      'Sensor is reporting normally'}
                    {sensor.healthStatus === SensorsHealthStatus.HealthStatusStale &&
                      'Sensor has not reported recently'}
                    {sensor.healthStatus === SensorsHealthStatus.HealthStatusMissing &&
                      'Sensor has stopped reporting'}
                    {sensor.healthStatus === SensorsHealthStatus.HealthStatusUnknown &&
                      'Sensor has never reported data'}
                  </div>
                </div>
              }
              position="bottom"
            >
              <div
                className={`h-2.5 w-2.5 cursor-help rounded-full ${getHealthStatusColor(sensor.healthStatus)}`}
              />
            </Tooltip>
            {isConnected && (
              <Tooltip content="Real-time updates connected" position="bottom">
                <span className="cursor-help text-xs text-emerald-600 dark:text-emerald-400">
                  LIVE
                </span>
              </Tooltip>
            )}
          </div>
          <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
            {sensor.lastSeen
              ? `Last updated ${formatRelativeTime(sensor.lastSeen)}`
              : 'No recent data'}
          </p>
          {sensor.humanId && (
            <button
              onClick={() => navigator.clipboard.writeText(sensor.humanId || '')}
              className="group mt-1 flex items-center gap-1.5 text-xs text-gray-400 transition-colors hover:text-gray-600 dark:text-zinc-600 dark:hover:text-zinc-400"
              title="Copy sensor ID"
            >
              <span className="font-mono">{sensor.humanId}</span>
              <Copy className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleToggleFavorite}
            className={`flex items-center justify-center rounded-lg border p-2 transition-all ${
              sensor.isFavorite
                ? 'border-amber-300 bg-amber-500/10 text-amber-500 hover:border-amber-400 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400'
                : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-amber-500 dark:border-white/[0.06] dark:bg-[#18181b] dark:text-zinc-500 dark:hover:border-white/[0.1] dark:hover:bg-[#1f1f23] dark:hover:text-amber-400'
            }`}
            title={sensor.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Star className="h-4 w-4" fill={sensor.isFavorite ? 'currentColor' : 'none'} />
          </button>
          <button
            onClick={handleOpenEditModal}
            className="flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-900 transition-all hover:border-gray-300 hover:bg-gray-50 dark:border-white/[0.06] dark:bg-[#18181b] dark:text-white dark:hover:border-white/[0.1] dark:hover:bg-[#1f1f23]"
            title="Edit sensor"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            onClick={refetchData}
            disabled={isDataFetching}
            className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-900 transition-all hover:border-gray-300 hover:bg-gray-50 disabled:opacity-50 dark:border-white/[0.06] dark:bg-[#18181b] dark:text-white dark:hover:border-white/[0.1] dark:hover:bg-[#1f1f23]"
          >
            <RefreshCw className={`h-4 w-4 ${isDataFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Current value card */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-white/[0.06] dark:bg-[#18181b]">
        <p className="text-sm font-medium text-gray-500 dark:text-zinc-500">Current Value</p>
        <div className="mt-2 flex items-baseline gap-2">
          <span
            className={`text-5xl font-semibold tabular-nums ${
              sensor.unitColor ? '' : 'text-gray-900 dark:text-white'
            }`}
            style={sensor.unitColor ? { color: sensor.unitColor } : undefined}
          >
            {formatValue(sensor.value)}
          </span>
          {sensor.unitSymbol && (
            <span className="text-2xl text-gray-500 dark:text-zinc-500">
              {formatUnit(sensor.unitSymbol)}
            </span>
          )}
        </div>
      </div>

      {/* Statistics */}
      {stats && (
        <div
          className={`grid grid-cols-3 gap-4 transition-opacity duration-200 ${
            isDataFetching ? 'opacity-60' : ''
          }`}
        >
          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-white/[0.06] dark:bg-[#18181b]">
            <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">
              {chartType === 'histogram' ? 'Min (per bucket)' : 'Min'}
            </p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900 dark:text-white">
              {formatValue(stats.min)}{' '}
              <span className="text-sm font-normal text-gray-500 dark:text-zinc-500">
                {formatUnit(sensor.unitSymbol)}
              </span>
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-white/[0.06] dark:bg-[#18181b]">
            <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">
              {chartType === 'histogram' ? 'Total' : 'Average'}
            </p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900 dark:text-white">
              {formatValue(stats.avg)}{' '}
              <span className="text-sm font-normal text-gray-500 dark:text-zinc-500">
                {formatUnit(sensor.unitSymbol)}
              </span>
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-white/[0.06] dark:bg-[#18181b]">
            <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">
              {chartType === 'histogram' ? 'Max (per bucket)' : 'Max'}
            </p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900 dark:text-white">
              {formatValue(stats.max)}{' '}
              <span className="text-sm font-normal text-gray-500 dark:text-zinc-500">
                {formatUnit(sensor.unitSymbol)}
              </span>
            </p>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
        {/* Chart controls */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 px-4 py-3 dark:border-white/[0.04]">
          {/* Left side: Chart type toggle and data point count */}
          <div className="flex items-center gap-3">
            {/* Chart type toggle */}
            <div className="flex rounded-lg bg-gray-100 p-0.5 dark:bg-zinc-800">
              <button
                onClick={() => handleChartTypeChange('line')}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  chartType === 'line'
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-zinc-700 dark:text-white'
                    : 'text-gray-600 hover:text-gray-900 dark:text-zinc-400 dark:hover:text-white'
                }`}
              >
                Line
              </button>
              <button
                onClick={() => handleChartTypeChange('histogram')}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  chartType === 'histogram'
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-zinc-700 dark:text-white'
                    : 'text-gray-600 hover:text-gray-900 dark:text-zinc-400 dark:hover:text-white'
                }`}
              >
                Histogram
              </button>
            </div>
            {dataPointCount > 0 && (
              <span className="text-xs text-gray-500 dark:text-zinc-500">
                {dataPointCount.toLocaleString()} {chartType === 'histogram' ? 'buckets' : 'points'}
              </span>
            )}
            {timeRange === 'custom' && <span className="text-xs text-blue-500">(zoomed)</span>}
          </div>

          {/* Right side: Time range and bucket size */}
          <div className="flex items-center gap-4">
            {/* Bucket size selector (only for histogram) */}
            {chartType === 'histogram' && (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-zinc-500">Bucket</span>
                  <div className="flex rounded-lg bg-gray-100 p-0.5 dark:bg-zinc-800">
                    {BUCKET_SIZES.map((bucket) => (
                      <button
                        key={bucket.value}
                        onClick={() => handleBucketSizeChange(bucket.value)}
                        className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                          bucketSize === bucket.value
                            ? 'bg-white text-gray-900 shadow-sm dark:bg-zinc-700 dark:text-white'
                            : 'text-gray-600 hover:text-gray-900 dark:text-zinc-400 dark:hover:text-white'
                        }`}
                      >
                        {bucket.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="h-4 w-px bg-gray-200 dark:bg-white/[0.08]" />
              </>
            )}

            {/* Time range selector */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-zinc-500">Range</span>
              <div className="flex rounded-lg bg-gray-100 p-0.5 dark:bg-zinc-800">
                {TIME_RANGES.map((range) => (
                  <button
                    key={range.value}
                    onClick={() => handleTimeRangeChange(range.value)}
                    className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                      timeRange === range.value
                        ? 'bg-white text-gray-900 shadow-sm dark:bg-zinc-700 dark:text-white'
                        : 'text-gray-600 hover:text-gray-900 dark:text-zinc-400 dark:hover:text-white'
                    }`}
                  >
                    {range.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Chart area */}
        <div className="h-80 p-4">
          {chartType === 'line' ? (
            <SensorChart
              data={lineChartData}
              startTime={chartStartTime}
              endTime={chartEndTime}
              unit={sensor.unitSymbol}
              color={sensor.unitColor}
              isLoading={isDataLoading}
              onZoom={handleZoom}
              onResetZoom={handleResetZoom}
            />
          ) : (
            <HistogramChart
              data={histogramChartData}
              startTime={chartStartTime}
              endTime={chartEndTime}
              unit={sensor.unitSymbol}
              color={sensor.unitColor}
              isLoading={isDataLoading}
              onZoom={handleZoom}
              onResetZoom={handleResetZoom}
            />
          )}
        </div>
      </div>

      {/* Sensor details */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
        <div className="border-b border-gray-200 px-4 py-3 dark:border-white/[0.04]">
          <h2 className="text-sm font-medium text-gray-900 dark:text-white">Details</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
          {/* Sensor ID */}
          {sensor.humanId && (
            <div className="group flex items-start gap-3 border-b border-r border-gray-100 p-4 dark:border-white/[0.04]">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-500/10">
                <Hash className="h-4 w-4 text-blue-500" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">Sensor ID</p>
                <div className="mt-0.5 flex items-center gap-1.5">
                  <p className="truncate font-mono text-sm text-gray-900 dark:text-white">
                    {sensor.humanId}
                  </p>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(sensor.humanId || '');
                    }}
                    className="shrink-0 rounded p-0.5 text-gray-400 opacity-0 transition-opacity hover:bg-gray-100 hover:text-gray-600 group-hover:opacity-100 dark:text-zinc-500 dark:hover:bg-white/[0.06] dark:hover:text-zinc-300"
                    title="Copy to clipboard"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Provider */}
          {sensor.provider && (
            <div className="flex items-start gap-3 border-b border-r border-gray-100 p-4 dark:border-white/[0.04]">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-purple-500/10">
                <Server className="h-4 w-4 text-purple-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">Provider</p>
                <p className="mt-0.5 truncate text-sm font-medium capitalize text-gray-900 dark:text-white">
                  {sensor.provider}
                </p>
              </div>
            </div>
          )}

          {/* Unit */}
          {sensor.unitSymbol && (
            <div className="flex items-start gap-3 border-b border-r border-gray-100 p-4 dark:border-white/[0.04]">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-cyan-500/10">
                <Ruler className="h-4 w-4 text-cyan-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">Unit</p>
                <p className="mt-0.5 truncate text-sm font-medium text-gray-900 dark:text-white">
                  {sensor.unitLabel
                    ? `${sensor.unitLabel} (${sensor.unitSymbol})`
                    : sensor.unitSymbol}
                </p>
              </div>
            </div>
          )}

          {/* Health Status */}
          <div className="flex items-start gap-3 border-b border-r border-gray-100 p-4 dark:border-white/[0.04]">
            <div
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                sensor.healthStatus === SensorsHealthStatus.HealthStatusLive
                  ? 'bg-emerald-500/10'
                  : sensor.healthStatus === SensorsHealthStatus.HealthStatusStale
                    ? 'bg-yellow-500/10'
                    : sensor.healthStatus === SensorsHealthStatus.HealthStatusMissing
                      ? 'bg-red-500/10'
                      : 'bg-gray-500/10'
              }`}
            >
              <Activity
                className={`h-4 w-4 ${
                  sensor.healthStatus === SensorsHealthStatus.HealthStatusLive
                    ? 'text-emerald-500'
                    : sensor.healthStatus === SensorsHealthStatus.HealthStatusStale
                      ? 'text-yellow-500'
                      : sensor.healthStatus === SensorsHealthStatus.HealthStatusMissing
                        ? 'text-red-500'
                        : 'text-gray-500'
                }`}
              />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">Health</p>
              <div className="mt-0.5 flex items-center gap-1.5">
                <span
                  className={`h-2 w-2 rounded-full ${getHealthStatusColor(sensor.healthStatus)}`}
                />
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {getHealthStatusLabel(sensor.healthStatus)}
                </span>
              </div>
            </div>
          </div>

          {/* Reporting Interval */}
          {sensor.reportingInterval && (
            <div className="flex items-start gap-3 border-b border-r border-gray-100 p-4 dark:border-white/[0.04]">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-500/10">
                <Clock className="h-4 w-4 text-orange-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">Interval</p>
                <p className="mt-0.5 text-sm font-medium text-gray-900 dark:text-white">
                  Every {sensor.reportingInterval}s
                </p>
              </div>
            </div>
          )}

          {/* Alert Threshold */}
          {sensor.alertThreshold && (
            <div className="flex items-start gap-3 border-b border-r border-gray-100 p-4 dark:border-white/[0.04]">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-500/10">
                <Bell className="h-4 w-4 text-red-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">Alert</p>
                <p className="mt-0.5 text-sm font-medium text-gray-900 dark:text-white">
                  After {sensor.alertThreshold}s
                </p>
              </div>
            </div>
          )}

          {/* Monotonic Offset */}
          {sensor.isMonotonic && (
            <div className="flex items-start gap-3 border-b border-r border-gray-100 p-4 dark:border-white/[0.04]">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10">
                <TrendingUp className="h-4 w-4 text-emerald-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">Offset</p>
                <p className="mt-0.5 text-sm font-medium text-gray-900 dark:text-white">
                  {sensor.monotonicOffset !== undefined && sensor.monotonicOffset > 0
                    ? `+${formatValue(sensor.monotonicOffset)}${formatUnit(sensor.unitSymbol)}`
                    : 'None'}
                </p>
              </div>
            </div>
          )}

          {/* Created date */}
          {sensor.createdAt && (
            <div className="flex items-start gap-3 border-b border-r border-gray-100 p-4 dark:border-white/[0.04]">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-500/10">
                <Calendar className="h-4 w-4 text-gray-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">Created</p>
                <p className="mt-0.5 text-sm font-medium text-gray-900 dark:text-white">
                  {new Date(sensor.createdAt).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-white/[0.06] dark:bg-[#18181b]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500 dark:text-zinc-500">
              Reporting Cadence
            </p>
            <p className="mt-1 text-sm text-gray-500 dark:text-zinc-500">
              Last 14 days, based on report timestamps.
            </p>
          </div>
          <div
            className={`text-sm font-medium ${getReportingStabilityTone(reportingSummary?.stability)}`}
          >
            {getReportingStabilityLabel(reportingSummary?.stability)}
          </div>
        </div>

        {reportingSummary ? (
          (reportingSummary.reportCount ?? 0) > 0 ? (
            <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-lg bg-gray-50 p-4 dark:bg-white/[0.03]">
                <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">Typical</p>
                <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                  {formatReportingDuration(reportingSummary.medianIntervalSeconds)}
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-zinc-500">Median interval</p>
              </div>
              <div className="rounded-lg bg-gray-50 p-4 dark:bg-white/[0.03]">
                <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">Usual Max</p>
                <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                  {formatReportingDuration(reportingSummary.p90IntervalSeconds)}
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-zinc-500">90th percentile</p>
              </div>
              <div className="rounded-lg bg-gray-50 p-4 dark:bg-white/[0.03]">
                <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">Longest Gap</p>
                <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                  {formatReportingDuration(reportingSummary.longestGapSeconds)}
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-zinc-500">
                  {reportingSummary.reportCount?.toLocaleString() ?? 0} reports
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 p-4 dark:bg-white/[0.03]">
                <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">
                  Suggested Thresholds
                </p>
                <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">
                  Stale:{' '}
                  {reportingSummary.suggestedReportingIntervalSeconds
                    ? formatReportingDuration(reportingSummary.suggestedReportingIntervalSeconds)
                    : '—'}
                </p>
                <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">
                  Missing:{' '}
                  {reportingSummary.suggestedAlertThresholdSeconds
                    ? formatReportingDuration(reportingSummary.suggestedAlertThresholdSeconds)
                    : '—'}
                </p>
              </div>
            </div>
          ) : (
            <div className="mt-5 rounded-lg bg-gray-50 px-4 py-3 text-sm text-gray-500 dark:bg-white/[0.03] dark:text-zinc-400">
              No reports were recorded in the last 14 days.
            </div>
          )
        ) : (
          <div className="mt-5 rounded-lg bg-gray-50 px-4 py-3 text-sm text-gray-500 dark:bg-white/[0.03] dark:text-zinc-400">
            Reporting summary is being refreshed.
          </div>
        )}
      </div>

      {/* Offset History Section - only shown for monotonic sensors with history */}
      {sensor.isMonotonic && offsetHistoryData?.events && offsetHistoryData.events.length > 0 && (
        <div className="rounded-xl border border-gray-200/80 bg-white shadow-sm dark:border-white/[0.06] dark:bg-[#111113]">
          <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-4 dark:border-white/[0.04]">
            <History className="h-4 w-4 text-gray-500" />
            <h2 className="text-sm font-medium text-gray-900 dark:text-white">Offset History</h2>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-white/[0.06] dark:text-zinc-400">
              {offsetHistoryData.events.length} event
              {offsetHistoryData.events.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-white/[0.04]">
            {offsetHistoryData.events.map((event) => (
              <div key={event.id} className="flex items-center justify-between px-5 py-3 text-sm">
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-7 w-7 items-center justify-center rounded-lg ${event.isManual ? 'bg-blue-500/10' : 'bg-amber-500/10'}`}
                  >
                    {event.isManual ? (
                      <Pencil
                        className={`h-3.5 w-3.5 ${event.isManual ? 'text-blue-500' : 'text-amber-500'}`}
                      />
                    ) : (
                      <RefreshCw className="h-3.5 w-3.5 text-amber-500" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {event.isManual ? 'Manual adjustment' : 'Auto-corrected reset'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-zinc-500">
                      {event.appliedAt && formatRelativeTime(new Date(event.appliedAt))}
                      {!event.isManual &&
                        event.previousValue !== undefined &&
                        ` · ${formatValue(event.previousValue)} → ${formatValue(event.newRawValue)}`}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-emerald-600 dark:text-emerald-400">
                    +{formatValue(event.offsetApplied)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-zinc-500">
                    Total: {formatValue(event.totalOffset)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Edit Modal - uses snapshot to avoid re-renders from live sensor updates */}
      {editingSensor && (
        <SensorEditModal sensor={editingSensor} isOpen={true} onClose={handleCloseEditModal} />
      )}
    </div>
  );
}
